export type BlogPost = {
  slug: string;
  title: string;
  date: string;
  meta: string;
  excerpt: string;
  sections: { heading?: string; subheading?: string; paragraphs?: string[]; items?: string[] }[];
};

export const blogPosts: BlogPost[] = [
  {
    slug: 'how-to-select-fog',
    title: 'How to Select the Right Fiber Optic Gyroscope for Your Application',
    date: 'March 15, 2026',
    meta: 'Selection Guide · 12 min read',
    excerpt:
      'A comprehensive guide covering bias stability, ARW, bandwidth, and environmental considerations for FOG selection.',
    sections: [
      {
        paragraphs: [
          'Choosing the right fiber optic gyroscope (FOG) is one of the most critical decisions in inertial navigation system design. With specifications that span several orders of magnitude across performance grades, understanding what each parameter means for your application is essential. This guide walks you through the key specifications and how to match them to your requirements.'
        ]
      },
      {
        heading: 'Understanding Key FOG Specifications',
        subheading: 'Bias Stability',
        paragraphs: [
          "Bias stability is arguably the most important FOG specification. It measures the gyroscope's ability to maintain a consistent zero-rate output over time and temperature variations. Expressed in degrees per hour (°/h), bias stability directly determines heading accuracy in navigation systems."
        ],
        items: [
          'Tactical grade: 1–10 °/h — suitable for short-duration missions and stabilization',
          'Navigation grade: 0.01–1 °/h — required for most autonomous navigation applications',
          'Strategic grade: <0.01 °/h — used in precision pointing and long-endurance navigation'
        ]
      },
      {
        subheading: 'Angular Random Walk (ARW)',
        paragraphs: [
          'ARW quantifies the noise in the gyroscope output, expressed in °/√h. Lower ARW means smoother angle output, which is critical for applications requiring fine angular resolution such as antenna pointing and optical stabilization. ARW and bias stability together define the overall accuracy envelope of the sensor.'
        ]
      },
      {
        subheading: 'Scale Factor Stability',
        paragraphs: [
          'Scale factor stability indicates how accurately the gyroscope converts angular rate into its output signal. Expressed in parts per million (ppm), it affects velocity and position accuracy in navigation calculations. For most navigation applications, scale factor stability better than 100 ppm is expected, while strategic-grade applications require stability below 10 ppm.'
        ]
      },
      {
        subheading: 'Bandwidth',
        paragraphs: [
          'Bandwidth defines the frequency range over which the gyroscope can accurately measure angular rate. Higher bandwidth is necessary for applications with fast dynamics, such as missile guidance and active stabilization platforms. Typical FOG bandwidths range from 100 Hz to over 1000 Hz depending on the design.'
        ]
      },
      {
        heading: 'Matching FOG Performance to Applications',
        subheading: 'Tactical Grade Applications',
        paragraphs: ['Tactical-grade FOGs with bias stability of 1–10 °/h are well-suited for:'],
        items: [
          'UAV attitude heading reference systems (AHRS)',
          'Short-range missile guidance',
          'Camera and antenna stabilization',
          'Robotics and autonomous ground vehicles'
        ]
      },
      {
        subheading: 'Navigation Grade Applications',
        paragraphs: ['Navigation-grade FOGs with bias stability of 0.01–1 °/h serve applications that demand sustained accuracy:'],
        items: [
          'Long-endurance UAV and maritime navigation',
          'Autonomous driving and ADAS dead reckoning',
          'Oil and gas downhole surveying (MWD/LWD)',
          'Tunnel and underground mapping'
        ]
      },
      {
        subheading: 'Strategic Grade Applications',
        paragraphs: ['Strategic-grade FOGs with bias stability below 0.01 °/h are reserved for the most demanding missions:'],
        items: [
          'Ship inertial navigation systems (INS)',
          'Satellite and space vehicle attitude control',
          'Precision surveying and geodetic instruments'
        ]
      },
      {
        heading: 'Environmental Considerations',
        subheading: 'Temperature Range',
        paragraphs: [
          'Operating temperature range is a critical factor, especially for outdoor and downhole applications. Standard FOGs are rated for -40°C to +60°C, while extended-range models can operate from -55°C to +85°C. For oil and gas MWD applications, special high-temperature FOGs rated to +175°C or higher are available.'
        ]
      },
      {
        subheading: 'Vibration and Shock',
        paragraphs: [
          'Vibration and shock resistance directly impact real-world bias stability. A FOG that performs well on a rate table may exhibit significantly degraded performance under vibration. Key specifications to evaluate include:'
        ],
        items: [
          'Vibration-induced bias shift under sinusoidal and random vibration profiles',
          'Operational shock survival (typically 30–100 g)',
          'Vibration rectification error (VRE) for precision applications'
        ]
      },
      {
        heading: 'Interface and Integration',
        paragraphs: [
          'FOG output interfaces affect system integration complexity. Common options include:',
          'Consider your system\'s data acquisition architecture, cable length requirements, and synchronization needs when selecting the interface type.'
        ],
        items: [
          'RS-422: The most common digital interface, reliable over moderate cable lengths',
          'Ethernet: Enables high-speed data transfer and networked architectures',
          'PTP (IEEE 1588): Provides precise time synchronization for multi-sensor systems',
          'Analog: Legacy interface still used in some military and aerospace systems'
        ]
      },
      {
        heading: 'Why Choose GyroNavi FOGs',
        paragraphs: [
          'GyroNavi offers one of the most comprehensive FOG portfolios in the industry, covering tactical to strategic-grade performance. Key advantages include:'
        ],
        items: [
          'Full performance range: Bias stability from 2 °/h down to 0.003 °/h across 30+ FOG models',
          'In-house manufacturing: Complete production from fiber winding to final calibration under one roof',
          'Custom configurations: OEM-specific mechanical, electrical, and firmware tailoring',
          'Rigorous calibration: Every unit undergoes multi-temperature and multi-rate calibration on precision rate tables',
          'Global support: Technical assistance and export compliance for international customers'
        ]
      }
    ]
  },
  {
    slug: 'mems-vs-fog-comparison',
    title: 'MEMS vs FOG: Choosing the Right Gyroscope Technology',
    date: 'February 28, 2026',
    meta: 'Technology Comparison · 10 min read',
    excerpt:
      'Detailed comparison of MEMS and fiber optic gyroscope technologies across performance, cost, SWaP, and reliability.',
    sections: [
      {
        paragraphs: [
          'The choice between MEMS (Micro-Electro-Mechanical Systems) and FOG (Fiber Optic Gyroscope) technologies is one of the most common decisions facing inertial system designers. While both technologies measure angular rate, they operate on fundamentally different principles and occupy distinct positions in the performance-cost landscape. This article provides a detailed, objective comparison to help you make the right choice.'
        ]
      },
      {
        heading: 'Technology Overview',
        subheading: 'How FOG Works',
        paragraphs: [
          'A fiber optic gyroscope operates on the Sagnac effect. Light from a laser source is split into two beams that travel in opposite directions through a coil of optical fiber. When the sensor rotates, the beam traveling in the direction of rotation experiences a longer optical path than the counter-propagating beam, creating a phase difference proportional to the angular rate. This phase shift is detected interferometrically, providing a highly stable and drift-resistant angular rate measurement.'
        ]
      },
      {
        subheading: 'How MEMS Works',
        paragraphs: [
          'MEMS gyroscopes use vibrating microstructures fabricated on silicon wafers. The Coriolis effect causes a secondary vibration when the sensor rotates, which is detected capacitively or piezoelectrically. MEMS sensors benefit from semiconductor manufacturing techniques, enabling small size, low cost, and high production volume — but their mechanical nature introduces drift and noise characteristics that differ fundamentally from optical approaches.'
        ]
      },
      {
        heading: 'Performance Comparison',
        paragraphs: [
          'The following table summarizes key performance differences between typical MEMS and FOG gyroscopes across common grades.'
        ]
      },
      {
        heading: 'SWaP Analysis',
        paragraphs: [
          'Size, Weight, and Power (SWaP) is often the deciding factor in applications where platform constraints are tight:',
          'For SWaP-constrained platforms such as small UAVs, MEMS is often the only viable option. For larger platforms where performance is paramount, FOGs provide superior accuracy.'
        ]
      },
      {
        heading: 'Cost Considerations',
        paragraphs: [
          'Cost differences between MEMS and FOG are substantial:',
          'MEMS benefits from economies of scale in semiconductor manufacturing, making high-volume production extremely cost-effective. FOG manufacturing involves precision fiber winding, optical assembly, and extensive calibration, which limits cost reduction at scale. However, for applications where navigation accuracy prevents costly mission failures, the total cost of ownership often favors FOG.'
        ]
      },
      {
        heading: 'Application Recommendations',
        subheading: 'When to Choose MEMS',
        paragraphs: [
          'MEMS is the preferred choice when size, weight, power, and cost are the primary constraints, such as consumer electronics, small UAVs, and high-volume industrial applications.'
        ]
      },
      {
        subheading: 'When to Choose FOG',
        paragraphs: [
          'FOG is the preferred choice when accuracy, long-term stability, and drift resistance are critical, such as navigation-grade INS, strategic platforms, and precision stabilization systems.'
        ]
      },
      {
        heading: 'GyroNavi Product Portfolio',
        paragraphs: [
          'GyroNavi offers both FOG and MEMS-based products, allowing us to provide unbiased recommendations based on your actual requirements.'
        ]
      }
    ]
  }
];

export const findPost = (slug: string) => blogPosts.find((post) => post.slug === slug);
